import React, { useEffect } from "react";
import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses } from "react-pro-sidebar";
import { connect } from "react-redux";
import { Link, matchPath, useLocation } from "react-router-dom";
import { NavbarBrand } from "reactstrap";

import getMainNav from "./AppSidebar/MainNav.js";
import getProductionNav from "./AppSidebar/ProductionNav.js";
import getQANav from "./AppSidebar/QANav.js";
import getStoreNav from "./AppSidebar/MainNav/StoreNav.js";

// Actions for Production maybe this should be all in the production nav file?
import { getProductionJobBoards } from "modules/production/job_boards/actions";
import { getProductionPpBoards } from "modules/production/pp_boards/actions";

import "./AppSidebar.css";
import { getCustomForms } from "modules/custom_forms/actions.js";
import HasRole from "modules/5_view_helpers/HasRole.js";
import { getCustomFormsMenus } from "modules/custom_forms/menus/actions.js";
import { toggleFullscreen } from "modules/3_reducers/web/ThemeOptions.js";
import _ from "lodash";
import getCustomFormMenuModuleNav from "./AppSidebar/CustomFormMenuModuleNav.js";

// Maps menu_location / parent_type to which sidebar to show
const menuLocationSidebarTypes = {
  production: "production",
  quality_assurance: "qa",
  store: "store",
};

// On a custom form records page, figure out which sidebar to use
const getCustomFormRecordsLocationType = (custom_form, custom_forms_menus, locationType) => {
  // Not on a records page, or form not loaded yet — use the default from the URL
  if (!custom_form) return locationType;

  // Form is directly on production / QA / store — use that module sidebar
  if (menuLocationSidebarTypes[custom_form.menu_location]) {
    return menuLocationSidebarTypes[custom_form.menu_location];
  }

  // Form isn't in a custom menu — stick with the default
  if (custom_form.menu_location != "custom_menu" || !custom_form.menu_id) {
    return locationType;
  }

  // Form lives in a custom menu — walk up to the top-level menu
  let menu = custom_forms_menus[custom_form.menu_id];
  while (menu?.parent_type == "custom_menu" && menu.parent_id) {
    menu = custom_forms_menus[menu.parent_id];
  }
  if (!menu) return locationType;

  // Top menu is a module — use the module sidebar
  if (menu.menu_type == "module") return "custom_forms_menu_module";

  // Top menu sits under a normal area (e.g. production, main menu) — use that sidebar
  return menuLocationSidebarTypes[menu.parent_type] || "main";
};

const mapStateToProps = (state) => {
  return {
    current_user: state.current_user,
    production_job_boards: state.production_job_boards,
    production_pp_boards: state.production_pp_boards,
    custom_forms: state.custom_forms,
    custom_forms_menus: state.custom_forms_menus,
    fullscreen: state.themeOptions?.fullscreen,
  };
};

const AppSidebar = (props) => {
  const { current_user, showMenu, setShowMenu, locationType, custom_forms, custom_forms_menus } = props;
  const location = useLocation();

  // Are we on a custom form records page? If so, pick sidebar from the form's menu setup
  const customFormRecordsMatch = matchPath(location.pathname, { path: "/custom_form/:custom_form_id/records" });
  const custom_form = customFormRecordsMatch && custom_forms[customFormRecordsMatch?.params?.custom_form_id];
  const effectiveLocationType = getCustomFormRecordsLocationType(custom_form, custom_forms_menus, locationType);

  // Split custom forms and menus into the groups each sidebar builder needs
  let displayCustomForms = _.filter(custom_forms);

  const main_nav_custom_forms_links = _.groupBy(
    _.filter(displayCustomForms, (form) =>
      ["administration", "main_menu", "drawing_office", "training", "hireages", "settings"].includes(form.menu_location)
    ),
    "menu_location"
  );

  let main_nav_menus = _(custom_forms_menus)
    .filter(
      (menu) =>
        menu.parent_type == "main_menu" ||
        menu.parent_type == "administration" ||
        menu.parent_type == "drawing_office" ||
        menu.parent_type == "training" ||
        menu.parent_type == "hireages" ||
        (menu.parent_type == "settings" && !menu.parent_id)
    )
    .groupBy("parent_type")
    .value();

  const production_nav_custom_forms_links = _.filter(displayCustomForms, (form) => form.menu_location == "production");
  const qa_nav_custom_forms_links = _.filter(displayCustomForms, (form) => form.menu_location == "quality_assurance");
  const store_nav_custom_forms_links = _.filter(displayCustomForms, (form) => form.menu_location == "store");

  const production_nav_menus = _.filter(custom_forms_menus, (menu) => menu.parent_type == "production");
  const qa_nav_menus = _.filter(custom_forms_menus, (menu) => menu.parent_type == "quality_assurance");
  const store_nav_menus = _.filter(custom_forms_menus, (menu) => menu.parent_type == "store");

  useEffect(() => {
    if (effectiveLocationType == "production") {
      props.getProductionJobBoards();
      props.getProductionPpBoards();
    }
    props.getCustomForms(true);
    props.getCustomFormsMenus();
  }, [effectiveLocationType]);

  let menuLabel = "Menu";

  const CustomFormsRoles = HasRole({ resource_type: "CustomForm" });

  let display_custom_forms = _.toArray(custom_forms);
  display_custom_forms = _.compact(display_custom_forms);
  display_custom_forms = _.filter(display_custom_forms, (form) => {
    return (form.include_parent_index_page || !form.parent_type) && !form.archived && form.menu_location == "main_menu";
  });

  // Build the sidebar links for whichever area we're in
  let sidebarLinks = getMainNav(current_user?.current_project_id, main_nav_custom_forms_links, main_nav_menus);
  if (effectiveLocationType == "production") {
    const { production_job_boards, production_pp_boards } = props;
    menuLabel = "Production Menu";
    sidebarLinks = getProductionNav(production_job_boards, production_pp_boards, production_nav_custom_forms_links, production_nav_menus);
  } else if (effectiveLocationType == "qa") {
    menuLabel = "QA Menu";
    sidebarLinks = getQANav(current_user?.current_project_id, qa_nav_custom_forms_links, qa_nav_menus);
  } else if (effectiveLocationType == "store") {
    menuLabel = "Store Menu";
    sidebarLinks = getStoreNav(store_nav_custom_forms_links, store_nav_menus);
  } else if (effectiveLocationType == "custom_forms_menu_module") {
    const moduleMatch = matchPath(location.pathname, { path: "/custom_forms/menus/:menu_id/module", exact: true });
    let moduleMenuId = moduleMatch?.params?.menu_id;

    if (!moduleMenuId && custom_form?.menu_id) {
      let menu = custom_forms_menus[custom_form.menu_id];
      while (menu && menu.menu_type != "module" && menu.parent_type == "custom_menu" && menu.parent_id) {
        menu = custom_forms_menus[menu.parent_id];
      }
      moduleMenuId = menu?.id;
    }

    const nav = getCustomFormMenuModuleNav(custom_forms_menus, moduleMenuId);
    menuLabel = nav?.menuLabel;
    sidebarLinks = nav?.sidebarLinks;
  } else if (effectiveLocationType == "custom_form_inside_a_menu") {
  } else {
    // Main sidebar extras
    if (CustomFormsRoles) {
      sidebarLinks.push({
        icon: "fa fa-table",
        label: "Custom Forms",
        to: `/custom_forms`,
      });
    }

    sidebarLinks.push({
      icon: "fa fa-th-large",
      label: "Portal",
      to: "/portal",
    });
  }

  return (
    <Sidebar
      onBackdropClick={() => setShowMenu(false)}
      toggled={showMenu}
      breakPoint="md"
      rootStyles={{
        [`.${sidebarClasses.container}`]: {
          backgroundColor: "white",
        },
      }}
    >
      <NavbarBrand className="d-none d-md-block pl-4">
        <Link to="/">
          <img src="/assets/lion-logo-HD.png" width="130" alt="logo" />
        </Link>
      </NavbarBrand>
      <div className="sidebar-heading pl-4">{menuLabel}</div>
      <Menu
        menuItemStyles={{
          button: ({ level, active, disabled }) => {
            // only apply styles on first level elements of the tree
            if (level != 0) {
              return {
                paddingLeft: "20px",
                height: "unset",
                color: "grey",
              };
            } else {
              return {
                height: "unset",
              };
            }
          },
        }}
      >
        {_.map(sidebarLinks, (navItem, i) => {
          return <SidebarMenuItems key={i} navItem={navItem} />;
        })}
      </Menu>
    </Sidebar>
  );
};

export default connect(mapStateToProps, { getProductionJobBoards, getProductionPpBoards, getCustomForms, getCustomFormsMenus, toggleFullscreen })(
  AppSidebar
);

const SidebarMenuItems = ({ navItem }) => {
  // Has nested items — render as expandable submenu
  if (navItem.content) {
    return (
      <SubMenu label={navItem.label} icon={<i className={navItem.icon} />}>
        {_.map(navItem.content, (subNavItem, i) => (
          <SidebarMenuItems key={i} navItem={subNavItem} />
        ))}
      </SubMenu>
    );
  }

  // Single link — external href or internal react-router link
  return (
    <MenuItem icon={<i className={navItem.icon} />} component={navItem.external ? <a href={navItem.to} /> : <Link to={navItem.to} />}>
      {navItem.label}
    </MenuItem>
  );
};
